import { defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch, } from 'vue';
export default defineComponent({
    name: 'AlbumViewer',
    props: {
        album: {
            type: Object,
            required: true,
        },
        initialIndex: {
            type: Number,
            default: 0,
        },
        closeLabel: {
            type: String,
            default: 'Close',
        },
        prevLabel: {
            type: String,
            default: 'Previous photo',
        },
        nextLabel: {
            type: String,
            default: 'Next photo',
        },
        photoCountLabel: {
            type: String,
            default: 'photos',
        },
    },
    emits: ['close'],
    setup(props, { emit }) {
        const panel = ref(null);
        const thumbRail = ref(null);
        const currentIndex = ref(props.initialIndex);
        const previousFocus = ref(null);
        const wrap = (i) => {
            const len = props.album.photos.length;
            return ((i % len) + len) % len;
        };
        const next = () => { currentIndex.value = wrap(currentIndex.value + 1); };
        const prev = () => { currentIndex.value = wrap(currentIndex.value - 1); };
        const goTo = (i) => { currentIndex.value = wrap(i); };
        const close = () => { emit('close'); };
        const preloadAdjacent = () => {
            [-1, 1].forEach((delta) => {
                const idx = wrap(currentIndex.value + delta);
                if (idx === currentIndex.value)
                    return;
                const img = new Image();
                img.src = props.album.photos[idx];
            });
        };
        const scrollActiveThumbIntoView = () => {
            if (!thumbRail.value)
                return;
            const active = thumbRail.value.querySelector('.viewer__thumb--active');
            if (active) {
                active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            }
        };
        const onKeydown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                close();
            }
            else if (e.key === 'ArrowRight') {
                e.preventDefault();
                next();
            }
            else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                prev();
            }
            else if (e.key === 'Tab') {
                // Simple focus trap: keep focus inside panel
                if (!panel.value)
                    return;
                const focusables = panel.value.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
                if (focusables.length === 0)
                    return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
                else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        watch(currentIndex, () => {
            preloadAdjacent();
            nextTick(scrollActiveThumbIntoView);
        });
        onMounted(() => {
            previousFocus.value = document.activeElement || null;
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', onKeydown);
            nextTick(() => {
                const closeBtn = panel.value?.querySelector('.viewer__close');
                closeBtn?.focus();
                scrollActiveThumbIntoView();
                preloadAdjacent();
            });
        });
        onBeforeUnmount(() => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKeydown);
            previousFocus.value?.focus();
        });
        return {
            panel,
            thumbRail,
            currentIndex,
            next,
            prev,
            goTo,
            close,
        };
    },
});
